import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { FaEnvelope, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';

const PageContainer = styled.div`
  padding-top: 2rem;
  padding-bottom: 5rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 35%, rgba(0, 213, 146, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(0, 213, 146, 0.03) 0%, transparent 40%);
    z-index: -1;
    top: 0;
    left: 0;
  }
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #f0fffa 0%, #e6f7f2 100%);
  padding: 5rem 0 6rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-bottom: 4rem;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(0, 213, 146, 0.1) 0%, transparent 40%),
      radial-gradient(circle at 80% 80%, rgba(0, 213, 146, 0.1) 0%, transparent 40%),
      url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2300d592' fill-opacity='0.03' fill-rule='evenodd'%3E%3Cpath d='M0 0h20L0 20z'/%3E%3C/g%3E%3C/svg%3E");
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(90deg, 
      transparent 0%,
      ${props => props.theme.colors.primary} 50%,
      transparent 100%
    );
    opacity: 0.3;
  }
`;

const BlockchainAnimation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  opacity: 0.5;
  pointer-events: none;
  
  .block {
    position: absolute;
    width: 30px;
    height: 30px;
    border: 1px solid rgba(0, 213, 146, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.03);
    animation: float 20s linear infinite;
    
    &::before {
      content: '';
      position: absolute;
      width: 10px;
      height: 10px;
      background: rgba(0, 213, 146, 0.1);
      border-radius: 2px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 2px;
      background: linear-gradient(90deg, transparent, rgba(0, 213, 146, 0.1), transparent);
      top: 50%;
      left: 30px;
    }
  }
  
  .block:nth-child(1) {
    top: 15%;
    left: 10%;
    animation-duration: 30s;
    animation-delay: 0s;
  }
  
  .block:nth-child(2) {
    top: 40%;
    left: 20%;
    animation-duration: 25s;
    animation-delay: 2s;
  }
  
  .block:nth-child(3) {
    top: 70%;
    left: 5%;
    animation-duration: 35s;
    animation-delay: 5s;
  }
  
  .block:nth-child(4) {
    top: 25%;
    right: 10%;
    animation-duration: 22s;
    animation-delay: 7s;
  }
  
  .block:nth-child(5) {
    top: 60%;
    right: 20%;
    animation-duration: 28s;
    animation-delay: 3s;
  }
  
  @keyframes float {
    0% {
      transform: translateX(0) translateY(0) rotate(0deg);
    }
    50% {
      transform: translateX(150px) translateY(20px) rotate(180deg);
    }
    100% {
      transform: translateX(0) translateY(0) rotate(360deg);
    }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2.5rem;
  margin-top: 4rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfoContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: ${props => props.theme.borderRadius};
  padding: 2.5rem;
  box-shadow: ${props => props.theme.boxShadow};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 213, 146, 0.1);
  height: 100%;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 6px;
    height: 60px;
    background: linear-gradient(to bottom, ${props => props.theme.colors.primary}, transparent);
    border-top-left-radius: ${props => props.theme.borderRadius};
    opacity: 0.8;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    right: -50px;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    opacity: 0.5;
  }
`;

const ContactInfoTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.secondary};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 3px;
    background: ${props => props.theme.colors.primary};
    border-radius: 3px;
  }
`;

const ContactInfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.8rem;
  
  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1.2rem;
    margin-top: 0.2rem;
    margin-right: 1rem;
    flex-shrink: 0;
  }
  
  div {
    flex: 1;
  }
  
  h4 {
    font-size: 1.1rem;
    margin-bottom: 0.4rem;
    color: ${props => props.theme.colors.secondary};
    font-weight: 600;
  }
  
  p {
    color: ${props => props.theme.colors.gray};
    line-height: 1.6;
    font-size: 0.95rem;
  }
  
  a {
    color: ${props => props.theme.colors.gray};
    text-decoration: none;
    transition: ${props => props.theme.transition};
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2.5rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  color: ${props => props.theme.colors.secondary};
  font-size: 1.2rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 213, 146, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    background: ${props => props.theme.colors.primary};
    color: white;
    border-color: transparent;
    box-shadow: 0 6px 12px rgba(0, 213, 146, 0.2);
  }
`;

const ContactForm = styled.form`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: ${props => props.theme.borderRadius};
  padding: 2.8rem;
  box-shadow: ${props => props.theme.boxShadow};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 213, 146, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 6px;
    height: 60px;
    background: linear-gradient(to bottom, ${props => props.theme.colors.primary}, transparent);
    border-top-right-radius: ${props => props.theme.borderRadius};
    opacity: 0.8;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at bottom right, rgba(0, 213, 146, 0.05), transparent 70%);
    z-index: 0;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.8rem;
    color: ${props => props.theme.colors.secondary};
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 40px;
      height: 3px;
      background: ${props => props.theme.colors.primary};
      border-radius: 3px;
    }
  }
  
  p {
    color: ${props => props.theme.colors.gray};
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const StatusMessage = styled.div`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  
  ${props => props.success ? `
    background-color: rgba(0, 213, 146, 0.1);
    color: #00a56b;
    border: 1px solid rgba(0, 213, 146, 0.3);
  ` : `
    background-color: rgba(255, 76, 76, 0.1);
    color: #e53e3e;
    border: 1px solid rgba(255, 76, 76, 0.3);
  `}
  
  svg {
    margin-right: 0.5rem;
    font-size: 1.1rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 500;
  font-size: 0.95rem;
`;

const InputWrapper = styled.div`
  position: relative;
  
  svg {
    position: absolute;
    top: 50%;
    left: 1rem;
    transform: translateY(-50%);
    color: ${props => props.theme.colors.gray};
    font-size: 1rem;
    z-index: 1;
    transition: ${props => props.theme.transition};
  }
  
  &:focus-within svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1.2rem 1rem ${props => props.hasIcon ? '2.8rem' : '1.2rem'};
  border: 1px solid ${props => props.theme.colors.lightGray};
  border-radius: 12px;
  font-family: ${props => props.theme.fonts.primary};
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 15px rgba(0, 213, 146, 0.3);
    transform: translateY(-2px);
  }
  
  &::placeholder {
    color: #adb5bd;
    font-size: 0.95rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem 1.2rem;
  border: 1px solid ${props => props.theme.colors.lightGray};
  border-radius: 12px;
  font-family: ${props => props.theme.fonts.primary};
  font-size: 1rem;
  min-height: 160px;
  resize: vertical;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 15px rgba(0, 213, 146, 0.3);
    transform: translateY(-2px);
  }
  
  &::placeholder {
    color: #adb5bd;
    font-size: 0.95rem;
  }
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const Spinner = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
  margin-right: 0.5rem;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SubmitButton = styled(Button)`
  min-width: 180px;
  position: relative;
  z-index: 1;
  overflow: hidden;
  padding: 0.9rem 2rem;
  font-weight: 600;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 60%);
    transform: rotate(45deg);
    z-index: -1;
    transition: all 0.5s ease;
  }
  
  &:hover::before {
    transform: rotate(180deg);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const FAQSection = styled.section`
  margin-top: 5rem;
  position: relative;
  z-index: 1;
  
  h2 {
    font-size: 2rem;
    text-align: center;
    margin-bottom: 3rem;
    color: ${props => props.theme.colors.secondary};
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -12px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      background: ${props => props.theme.colors.primary};
      border-radius: 3px;
    }
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FAQItem = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: ${props => props.theme.borderRadius};
  padding: 1.8rem 2rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 213, 146, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08);
  }
  
  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.8rem;
    color: ${props => props.theme.colors.secondary};
    font-weight: 600;
  }
  
  p {
    color: ${props => props.theme.colors.gray};
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://formspree.io/f/xldjrold', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        // Form successfully submitted
        setSubmitStatus({ success: true, message: 'Thank you! Your message has been sent successfully.' });
        // Reset form fields
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        // Server returned an error
        setSubmitStatus({
          success: false,
          message: data.error || 'Something went wrong. Please try again later.'
        });
      }
    } catch (error) {
      // Network error or other exception
      setSubmitStatus({
        success: false,
        message: 'Failed to connect to the server. Please check your internet connection and try again.'
      });
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>Contact Us | RDT Chain - Support, Partnerships & Inquiries</title>
      </Helmet>
      <BlockchainAnimation>
        <div className="block"></div>
        <div className="block"></div>
        <div className="block"></div>
        <div className="block"></div>
        <div className="block"></div>
      </BlockchainAnimation>

      <HeroSection>
        <Container>
          <SectionTitle
            subtitle="Contact Us"
            title="Get in Touch with RDT Chain"
            description="Have questions about our private blockchain or interested in partnership opportunities? Our team at RDT Chain Consortium, is here to help with your inquiries."
            center
          />
        </Container>
      </HeroSection>

      <Container>
        <div style={{ maxWidth: '650px', margin: '0 auto' }}>
          <ContactForm onSubmit={handleSubmit}>
            <FormHeader>
              <h3>Send Us a Message</h3>
              <p>Fill out the form below to inquire about our private blockchain solutions, and our team will respond as soon as possible</p>
            </FormHeader>

            {submitStatus && (
              <StatusMessage success={submitStatus.success}>
                {submitStatus.success ? (
                  <FaCheck />
                ) : (
                  <FaExclamationTriangle />
                )}
                {submitStatus.message}
              </StatusMessage>
            )}

            <FormRow>
              <FormGroup>
                <Label htmlFor="name">Full Name</Label>
                <InputWrapper>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    disabled={isSubmitting}
                  />
                </InputWrapper>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <InputWrapper>
                  <FaEnvelope />
                  <Input
                    hasIcon
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email address"
                    disabled={isSubmitting}
                  />
                </InputWrapper>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label htmlFor="subject">Subject</Label>
              <Input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder="What is your message about?"
                disabled={isSubmitting}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Message</Label>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Type your message here..."
                disabled={isSubmitting}
              />
            </FormGroup>

            <FormFooter>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner /> Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </SubmitButton>
            </FormFooter>
          </ContactForm>
        </div>

        <FAQSection>
          <h2>Frequently Asked Questions</h2>
          <FAQGrid>
            <FAQItem>
              <h3>What is RDT Chain?</h3>
              <p>RDT Chain is a private blockchain owned and operated by RDT Blockchain System, Inc. It provides enterprise-grade blockchain infrastructure with zero transaction fees and 5-second block confirmations.</p>
            </FAQItem>

            <FAQItem>
              <h3>Can I use RDT Chain for my business?</h3>
              <p>Yes, businesses can partner with RDT Blockchain System, Inc. to leverage our private blockchain infrastructure. Contact us through this form to discuss partnership opportunities.</p>
            </FAQItem>

            <FAQItem>
              <h3>Is RDT Chain open source?</h3>
              <p>While RDT Chain is a private blockchain, we do provide APIs and development tools for authorized partners. Our team can provide technical documentation upon request for approved integrations.</p>
            </FAQItem>

            <FAQItem>
              <h3>How does RDT Chain ensure security?</h3>
              <p>As a private blockchain owned by RDT Blockchain System, Inc., we employ a permissioned network with enterprise-grade security protocols, regular audits, and a dedicated security team to protect the network.</p>
            </FAQItem>
          </FAQGrid>
        </FAQSection>
      </Container>
    </PageContainer>
  );
};

export default Contact;